"use client";

import { useState } from "react";

interface RowKeys {
	keys: number[];
	/** The next key never handed out. */
	next: number;
}

const keysFrom = (start: number, count: number): RowKeys => ({
	keys: Array.from({ length: count }, (_, i) => start + i),
	next: start + count,
});

/**
 * React keys for an editable list whose items carry no id (starter prompts, model routes):
 * each row keeps its key while rows above it are removed, so its inputs keep their state and
 * focus. Call `added` and `removed` beside the update that adds or removes the row. A list
 * replaced from outside with a different length (a discarded draft, a loaded version) starts
 * over with new keys.
 */
export function useRowKeys(count: number) {
	const [rows, setRows] = useState(() => keysFrom(0, count));
	if (rows.keys.length !== count) {
		setRows(keysFrom(rows.next, count));
	}
	return {
		keys: rows.keys,
		added: () =>
			setRows((current) => ({
				keys: [...current.keys, current.next],
				next: current.next + 1,
			})),
		removed: (index: number) =>
			setRows((current) => ({
				...current,
				keys: current.keys.filter((_, i) => i !== index),
			})),
	};
}
